import { UserProfileMenu } from '@/components/layout/UserProfileMenu';
import { ActionBar } from '@/components/layout/ActionBar';
import React, { useCallback, useRef, useState, useEffect, useMemo } from 'react';
import { Image } from 'expo-image';
import { ActivityIndicator, RefreshControl, Platform } from 'react-native';
import { cssInterop } from 'nativewind';
import { useIsLargeScreen } from '@/hooks/useIsLargeScreen';
import { InfiniteData, useInfiniteQuery, useQueryClient } from '@tanstack/react-query';
import { PostsService } from '~/services/domain/PostsService';
import {
  PageParam,
  getNextPageSkipOrCursor,
  buildPaginationParams,
} from '@/hooks/usePaginationUtils';
import { FlashList } from '@shopify/flash-list';

import { PostCard } from '@/components/domain/posts/PostCard';
import { useIsFocused, useScrollToTop } from 'expo-router/react-navigation';
import { PostCardUpgrade } from '@/components/domain/posts/PostCardUpgrade';
import { PostsView, QuotaTypes } from '@/models/Post';
import {
  Search02Icon,
  Cancel02Icon,
  Pin02Icon as Pin02SolidIcon,
} from '@hugeicons-pro/core-solid-rounded';
import { LicenseNoIcon, Pin02Icon as Pin02StrokeIcon } from '@hugeicons-pro/core-stroke-rounded';
import { useTranslation } from 'react-i18next';
import { FeedType } from '~/constants/lists';
import { ScreenHeader } from '@/components/layout/ScreenHeader';
import { router, useLocalSearchParams } from 'expo-router';
import { PostSearch } from '~/components/domain/posts/PostSearch';
import { MessageView } from '@/components/ui/MessageView';
import { ErrorInListView } from '@/components/ui/ErrorInListView';
import { CommunitySelectorHeader } from '~/components/layout/CommunitySelectorHeader';
import { StagingApiBadge } from '@/components/ui/StagingApiBadge';
import { getFeedListItems } from '@/components/domain/posts/feedListItems';
import { HeaderMenuSelector, HeaderMenuSelectorTitle } from '@/components/ui/HeaderMenuSelector';
import { HeaderRefreshButton } from '@/components/ui/HeaderRefreshButton';
import { VStack } from '../../gluestack/vstack';
import { ButtonWrapper } from '@/components/ui/ButtonWrapper';
import { Box } from '@/components/gluestack/box';
import { CardWrapper } from '~/components/domain/posts/CardWrapper';
import { ContextSheet } from '@/components/ui/ContextSheet';
import { AddPost } from '@/components/domain/posts/AddPost';
import { FeedCreatePostFab } from '@/components/domain/posts/FeedCreatePostFab';
import { FeedComposerPrompt } from '@/components/domain/posts/FeedComposerPrompt';
import {
  useInfiniteListUtils,
  createSkeletonItems,
  clearStoredScrollPosition,
} from '@/hooks/useInfiniteListUtils';
import { useFlashListOptimizer } from '@/hooks/useFlashListOptimizer';
import { useHideOnScroll } from '@/hooks/useHideOnScroll';
import { useRemovePostFromCache } from '@/hooks/useQueryCacheUtils';
import { HStack } from '@/components/gluestack/hstack';
import { MyTypeFilterMenu } from '~/components/domain/common/MyTypeFilterMenu';
import { RadiusFilterMenu, useLocalFeedRadius } from '~/components/domain/posts/RadiusFilterMenu';
import { useDefaultFeed, type PinnableFeedType } from '~/hooks/useDefaultFeed';
import { useToastMessages } from '~/hooks/useToastMessages';
import { useRegisterRightPanelSlot } from '@/components/layout/RightPanelSlotContext';
import { FeedRightPanel } from '@/components/domain/posts/FeedRightPanel';
import { useAuth } from '@/components/providers/AuthProvider';
import { Member } from '@/models/Member';

cssInterop(Image, { className: 'style' });

interface FeedViewProps {
  feedType: FeedType;
  lastUsedFeed?: FeedType;
  /** Lists the posts using this hashtag as a drill-down page (back button, no feed controls) */
  hashtag?: string;
}

export function FeedScreen({ feedType, lastUsedFeed = 'global', hashtag }: FeedViewProps) {
  const params = useLocalSearchParams();
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const isLargeScreen = useIsLargeScreen();
  const isScreenActive = useIsFocused();
  const { accountStatus, currentCommunity } = useAuth();
  const { pageSize, onEndReachedThreshold } = useFlashListOptimizer({
    estimatedRowsOnScreen: { mobile: 6, desktop: 7 },
  });

  const flashListRef = useRef<React.ComponentRef<typeof FlashList<any>>>(null);

  const [searchQuery, setSearchQuery] = useState(() => {
    // Initialize search query with URL parameter if it exists
    const queryParam = params.q as string;
    return queryParam || '';
  });

  const [isAddPostSheetOpen, setIsAddPostSheetOpen] = useState(false);
  const { radiusKm, setRadiusKm, isLoaded: isRadiusLoaded } = useLocalFeedRadius();
  const { defaultFeed, setDefaultFeed, isDefaultFeed } = useDefaultFeed();
  const { showSuccessToast } = useToastMessages();

  const rightPanelContent = useMemo(() => <FeedRightPanel />, []);
  useRegisterRightPanelSlot(rightPanelContent);

  const feedListItems = useMemo(() => getFeedListItems(t), [t]);
  const currentMember = useMemo(() => {
    if (!accountStatus) return null;

    const member = new Member();
    member.Id = accountStatus.MemberId;
    member.UserName = accountStatus.UserName;
    member.IconUrl = accountStatus.IconUrl;

    return member;
  }, [accountStatus]);

  // Keep searchQuery in sync with the URL's ?q= param so subsequent navigations to the same
  // search route (e.g. clicking another popular term) swap the query without requiring a remount.
  // We only react to URL changes, not to local typing (which uses history.pushState and leaves
  // params.q unchanged) so we don't fight the PostSearch input.
  const urlQuery = (params.q as string) ?? '';
  const lastSyncedUrlQueryRef = useRef(urlQuery);
  useEffect(() => {
    if (feedType !== 'search') return;
    if (urlQuery !== lastSyncedUrlQueryRef.current) {
      lastSyncedUrlQueryRef.current = urlQuery;
      setSearchQuery(urlQuery);
    }
  }, [urlQuery, feedType]);

  const activeQuery = hashtag ?? searchQuery;
  const isSearchQueryUndefined = feedType === 'search' && activeQuery.trim() === '';
  const queryKey = [
    'posts',
    currentCommunity,
    feedType,
    activeQuery,
    feedType === 'local' ? radiusKm : undefined,
  ];

  // Registers scroll to top for when the tab is pressed
  useScrollToTop(flashListRef);

  const {
    data,
    error,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isRefetching,
    isPending,
    refetch,
  } = useInfiniteQuery({
    queryKey,
    queryFn: async ({ pageParam }) => {
      return await PostsService.getPosts(feedType, {
        ...buildPaginationParams(pageParam, pageSize),
        ...(feedType === 'search' ? { query: activeQuery } : {}),
        ...(feedType === 'local' ? { radiusKm } : {}),
      });
    },
    enabled:
      !!currentCommunity && !isSearchQueryUndefined && (feedType !== 'local' || isRadiusLoaded),
    initialPageParam: undefined as PageParam,
    getNextPageParam: getNextPageSkipOrCursor,
    select: useCallback((data: InfiniteData<PostsView, PageParam>) => {
      const items = data.pages.flatMap((page) => page.PostItems);
      return { ...data, items };
    }, []),
  });

  const {
    handleRefresh,
    isUserRefreshing,
    showHeaderRefetchIndicator,
    onScroll,
    onViewableItemsChanged,
  } = useInfiniteListUtils({
    queryKey,
    flashListRef,
    pageKey: hashtag
      ? `/hashtag/${hashtag.slice(1)}`
      : searchQuery
        ? `/feeds/${feedType}?q=${searchQuery}`
        : `/feeds/${feedType}`,
  });

  const { onScroll: onScrollHideTabBar, lockTabBar } = useHideOnScroll();

  const handleScroll = useCallback(
    (e: any) => {
      onScroll?.(e);
      onScrollHideTabBar(e);
    },
    [onScroll, onScrollHideTabBar]
  );

  const skeletonItems = useMemo(() => createSkeletonItems(6), []);

  // Stable identity so every visible PostCard isn't re-rendered (memo defeated)
  // whenever this screen re-renders (likes, pagination, refresh).
  const handlePostDeleted = useRemovePostFromCache();

  const firstOverQuotaIndex = useMemo(() => {
    if (!data?.items) return -1;
    return data.items.findIndex((item) => item.OverQuota !== QuotaTypes.None);
  }, [data?.items]);

  const itemCount = data?.pages?.[0]?.ItemCount ?? 0;
  const isMyTypeFiltered = data?.pages?.[0]?.IsMyTypeFiltered ?? null;
  const showFeedComposerPrompt = feedType !== 'search' && !!currentMember;

  const handleOpenAddPost = useCallback(() => {
    setIsAddPostSheetOpen(true);
  }, []);

  const feedComposerHeader = useMemo(() => {
    if (!showFeedComposerPrompt || !currentMember) return undefined;

    return (
      <FeedComposerPrompt
        member={currentMember}
        placeholder={t('post.placeholder')}
        accessibilityLabel={t('post.newPost')}
        onPress={handleOpenAddPost}
      />
    );
  }, [showFeedComposerPrompt, currentMember, t, handleOpenAddPost]);

  const navigateToFeed = (feed: FeedType) => {
    if (feed === feedType) return;

    queryClient.cancelQueries({ queryKey });

    // Sideways navigation between sibling feeds should not restore the destination's
    // previous scroll position; only stack-pop returns (e.g. back from a post) should.
    clearStoredScrollPosition(`/feeds/${feed}`);

    if (feed === 'search') {
      const fromParam = feedType !== 'search' ? `?from=${feedType}` : '';
      router.replace(`/feeds/search${fromParam}` as any);
    } else {
      router.replace(`/feeds/${feed}` as any);
    }
  };

  const isPinned = isDefaultFeed(feedType);

  const handleTogglePin = () => {
    if (isPinned && feedType !== 'global') {
      setDefaultFeed('global');
      showSuccessToast(t('feeds.unpinned'));
    } else if (isPinned) {
      showSuccessToast(t('feeds.alreadyDefault'));
    } else {
      setDefaultFeed(feedType as PinnableFeedType);
      showSuccessToast(t('feeds.pinned'));
    }
  };

  const PinButton = () => {
    if (feedType === 'search') return null;
    return (
      <ButtonWrapper
        icon={isPinned ? Pin02SolidIcon : Pin02StrokeIcon}
        size="xs"
        iconSize={18}
        variant="solid"
        action="default"
        className="-ml-2 -mr-1 md:-ml-1 md:-mr-1"
        iconClassName={isPinned ? 'color-primary-500' : 'color-typography-400'}
        onPress={handleTogglePin}
      />
    );
  };

  const ActionButtons = () => (
    <>
      {feedType !== 'search' && (
        <ButtonWrapper icon={Search02Icon} size="sm" onPress={() => navigateToFeed('search')} />
      )}
      {!isLargeScreen && feedType === 'search' && (
        <ButtonWrapper
          icon={Cancel02Icon}
          size="sm"
          action="neutral"
          iconClassName="color-typography-500"
          onPress={() => navigateToFeed(lastUsedFeed)}
        />
      )}
      <HeaderRefreshButton
        isVisible={!(feedType === 'search' && searchQuery === '')}
        isLargeScreen={isLargeScreen}
        isRefreshing={isUserRefreshing}
        isDisabled={isRefetching}
        onPress={handleRefresh}
      />
    </>
  );

  return (
    <VStack className="flex-1">
      <ScreenHeader
        options={{
          hideBackButton: !hashtag,
          title: hashtag ?? t(`feeds.${feedType}`),
          ...(hashtag
            ? {}
            : isLargeScreen
              ? {
                  headerTitle: () => (
                    <HeaderMenuSelectorTitle
                      value={feedType}
                      items={feedListItems}
                      title={t(`feeds.${feedType}`)}
                    />
                  ),
                  headerLeft: () => (
                    <HStack className="items-center gap-3">
                      <PinButton />
                      {feedType === 'local' && (
                        <RadiusFilterMenu radiusKm={radiusKm} onRadiusChange={setRadiusKm} />
                      )}
                      <MyTypeFilterMenu count={itemCount} isMyTypeFiltered={isMyTypeFiltered} />
                      {showHeaderRefetchIndicator ? <ActivityIndicator color="grey" /> : null}
                    </HStack>
                  ),
                  headerRight: () => <ActionButtons />,
                }
              : {
                  hideTitle: true,
                  headerLeft: () => (
                    <HStack className="items-center" space="sm">
                      <CommunitySelectorHeader />
                      <StagingApiBadge />
                    </HStack>
                  ),
                  headerRight: () => (
                    <Box className="-mt-1">
                      <UserProfileMenu />
                    </Box>
                  ),
                  headerBottom: () => (
                    <ActionBar
                      leftSection={
                        <HStack className="items-center gap-3 pr-3">
                          <HeaderMenuSelector
                            value={feedType}
                            onChange={navigateToFeed}
                            items={feedListItems}
                            triggerLabel={t(`feeds.${feedType}`)}
                          />
                          <PinButton />
                          <HStack className="items-center gap-3">
                            {feedType === 'local' && (
                              <RadiusFilterMenu radiusKm={radiusKm} onRadiusChange={setRadiusKm} />
                            )}
                            <MyTypeFilterMenu
                              count={itemCount}
                              isMyTypeFiltered={isMyTypeFiltered}
                            />
                          </HStack>
                        </HStack>
                      }
                      rightSection={<ActionButtons />}
                    />
                  ),
                }),
        }}
      />

      {feedType === 'search' && !hashtag && (
        <PostSearch
          searchQuery={searchQuery}
          onSearchQueryChange={(query) => {
            setSearchQuery(query);
            if (Platform.OS === 'web') {
              window.history.pushState(
                {},
                '',
                `/feeds/search${query.trim() !== '' ? `?q=${encodeURIComponent(query)}` : ''}`
              );
            }
          }}
          autoFocus={!urlQuery}
        />
      )}

      {(() => {
        if (error) {
          return (
            <ErrorInListView error={error} onRetry={() => refetch()} isRetrying={isRefetching} />
          );
        }
        if (isSearchQueryUndefined) {
          return <></>;
        }
        if (!isPending && (data?.items?.length ?? 0) === 0 && !showFeedComposerPrompt) {
          return <MessageView type="warning" text={t('feeds.empty')} icon={LicenseNoIcon} />;
        }
        return (
          <FlashList
            ref={flashListRef}
            data={isPending ? skeletonItems : data.items}
            ListHeaderComponent={feedComposerHeader}
            ListEmptyComponent={
              !isPending
                ? () => (
                    <MessageView
                      type="warning"
                      text={t('feeds.empty')}
                      icon={LicenseNoIcon}
                      listEmpty
                    />
                  )
                : undefined
            }
            renderItem={({ item, index }) => (
              <>
                <CardWrapper>
                  {isPending ? (
                    <PostCard />
                  ) : (
                    <PostCard
                      post={item}
                      flashListRef={flashListRef}
                      itemIndex={index}
                      onPostDeleted={handlePostDeleted}
                      onBeforeProgrammaticScroll={lockTabBar}
                      key={`post-${item.Id}`}
                    />
                  )}
                </CardWrapper>
                {!isPending &&
                  item.OverQuota !== QuotaTypes.None &&
                  (index === firstOverQuotaIndex || (index - 3) % 4 === 0) && (
                    <CardWrapper>
                      <PostCardUpgrade />
                    </CardWrapper>
                  )}
              </>
            )}
            keyExtractor={(item, index) => (isPending ? `s-${index}` : `post-${item.Id}`)}
            // Bucket recycling by cell shape: without this, photo posts recycle into
            // text posts (and vice versa), forcing a full subtree teardown per swap.
            // The -upgrade suffix mirrors renderItem's appended PostCardUpgrade so the
            // taller two-card cells get their own bucket.
            getItemType={(item, index) =>
              isPending
                ? 'skeleton'
                : `${item.IsSystem ? 'system' : item.IsPhoto ? 'photo' : 'text'}${
                    item.OverQuota !== QuotaTypes.None &&
                    (index === firstOverQuotaIndex || (index - 3) % 4 === 0)
                      ? '-upgrade'
                      : ''
                  }`
            }
            onEndReached={() => {
              if (!isPending && hasNextPage && isScreenActive && !isFetchingNextPage) {
                fetchNextPage();
              }
            }}
            maintainVisibleContentPosition={{
              autoscrollToTopThreshold: 10,
            }}
            onEndReachedThreshold={onEndReachedThreshold}
            ListFooterComponent={() => {
              if (!isFetchingNextPage) return null;
              return (
                <Box className="m-4">
                  <ActivityIndicator color="grey" />
                </Box>
              );
            }}
            refreshControl={
              <RefreshControl refreshing={isUserRefreshing} onRefresh={handleRefresh} />
            }
            onScroll={handleScroll}
            onViewableItemsChanged={onViewableItemsChanged}
            drawDistance={Platform.OS === 'ios' ? 500 : undefined}
          />
        );
      })()}
      {feedType !== 'search' && <FeedCreatePostFab onPress={handleOpenAddPost} />}

      {/* Add Post Sheet */}
      <ContextSheet
        isOpen={isAddPostSheetOpen}
        onClose={() => setIsAddPostSheetOpen(false)}
        contentComponent={AddPost}
        title={t('post.newPost')}
        payload={{
          onPostCreated: () => {
            setIsAddPostSheetOpen(false);
            setTimeout(() => {
              handleRefresh({ showRefreshingIndicator: false });
            }, 500); // Delay to ensure the scroll happens after the new post is added
          },
        }}
      />
    </VStack>
  );
}
